import { createRootRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { Layout } from '@/components/layout';

export const Route = createRootRoute({
  component: RootComponent,
});

// Exported (in addition to being wired up via `Route`) so it can be rendered
// directly in tests without needing a full router context.
export function RootComponent() {
  const { isAuthenticated, isLoading, fetchMe } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);
  const [checkingSetup, setCheckingSetup] = useState(true);

  // Public paths that must render without auth checks.
  //
  // /pay/* — all payment routes are intentionally public: /pay/$token (invoice
  //   view), /pay/success, /pay/cancelled. We use a prefix match rather than
  //   enumerating exact paths because /pay/$token is dynamic (the token segment
  //   can be any string). Security note: a prefix match is deliberately broader
  //   than exact matching — if a future route is accidentally placed under /pay/
  //   it would be treated as public. This is acceptable because: (a) TanStack
  //   Router's flat-file convention makes adding a protected route under /pay/
  //   a visible, deliberate act; (b) the payment epic's design intent is that
  //   ALL /pay/* routes serve unauthenticated clients; (c) there is no existing
  //   or planned protected content under /pay/. Reviewed during main↔epic sync,
  //   August 2026 — no tightening required.
  //
  // /privacy and /terms — linked from the public payment page footer; must be
  //   reachable without logging in.
  const isPublicPaymentPath =
    location.pathname.startsWith('/pay/') ||
    location.pathname === '/privacy' ||
    location.pathname === '/terms';

  // Check if setup is needed on initial load
  useEffect(() => {
    if (isPublicPaymentPath) {
      setNeedsSetup(false);
      setCheckingSetup(false);
      return;
    }
    const checkSetup = async () => {
      try {
        const response = await fetch('/api/setup/status');
        const data = await response.json();
        setNeedsSetup(data.needsSetup);
      } catch {
        // If check fails, assume setup not needed (API might be down)
        setNeedsSetup(false);
      } finally {
        setCheckingSetup(false);
      }
    };
    checkSetup();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch user info
  useEffect(() => {
    if (!needsSetup && !isPublicPaymentPath) {
      fetchMe();
    }
  }, [fetchMe, needsSetup, isPublicPaymentPath]);

  // Handle navigation based on auth and setup status
  useEffect(() => {
    if (checkingSetup) return;

    const currentPath = location.pathname;

    if (isPublicPaymentPath) return;

    // If setup is needed, redirect to setup page (unless already there)
    if (needsSetup && currentPath !== '/setup') {
      navigate({ to: '/setup' });
      return;
    }

    // If setup is done but user is on setup page, redirect to login
    if (!needsSetup && currentPath === '/setup') {
      navigate({ to: '/login' });
      return;
    }

    // Normal auth flow - redirect to login if not authenticated
    if (
      !needsSetup &&
      !isLoading &&
      !isAuthenticated &&
      currentPath !== '/login' &&
      currentPath !== '/setup'
    ) {
      navigate({ to: '/login' });
    }
  }, [isAuthenticated, isLoading, navigate, needsSetup, checkingSetup, location.pathname, isPublicPaymentPath]); // prettier-ignore

  // Public payment paths render bare — no Layout, no loading gate
  if (isPublicPaymentPath) {
    return <Outlet />;
  }

  // Show loading state while checking setup or auth
  if (checkingSetup || (isLoading && !needsSetup)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white font-bold text-2xl">A</span>
          </div>
          <p className="text-gray-500 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Setup page doesn't need layout
  if (needsSetup || location.pathname === '/setup') {
    return <Outlet />;
  }

  // If not authenticated, only the login page itself is safe to render.
  // For any other route, show a neutral loading state instead of the
  // route's Outlet — mounting a protected route component here (even
  // briefly, before the redirect effect above fires) would flash real
  // page structure/data-fetching for an unauthenticated visitor.
  if (!isAuthenticated) {
    if (location.pathname === '/login') {
      return <Outlet />;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white font-bold text-2xl">A</span>
          </div>
          <p className="text-gray-500 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}
